import { definePreset } from '@primeuix/themes';
import Material from '@primeuix/themes/material';
import type { PrimeNGConfigType } from 'primeng/config';
import { BD_MESSAGE_TOKENS, BD_SEMANTIC_TOKENS } from './bd-colors';
import { TABLE_TOOLBAR_HEADER } from './table-toolbar';
import { TRANSLATION_DE } from './translation/de';

export const PrimeNgMetaTheme = definePreset(Material, {
  semantic: {
    bd: BD_SEMANTIC_TOKENS,
    primary: {
      50: '{orange.50}',
      100: '{orange.100}',
      200: '{orange.200}',
      300: '{orange.300}',
      400: '{orange.400}',
      500: '{orange.500}',
      600: '{orange.600}',
      700: '{orange.700}',
      800: '{orange.800}',
      900: '{orange.900}',
      950: '{orange.950}',
    },
    colorScheme: {
      light: {
        primary: {
          contrastColor: '#000000',
        },
        surface: {
          0: '#ffffff',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}',
        },
      },
    },
  },
  components: {
    message: BD_MESSAGE_TOKENS,
    radiobutton: {
      root: {
        // The Material preset fills the checked radio's interior with
        // {primary.contrast.color}. This theme sets that contrast color to
        // black (for legible text on the orange primary), which turned the
        // selected radio into a black disc. A Material radio is a ring + dot
        // over the field surface, so pin the checked fill to the field
        // background — leaving only the orange ring and orange dot visible.
        checkedBackground: '{form.field.background}',
        checkedHoverBackground: '{form.field.background}',
      },
    },
    togglebutton: {
      // The Material preset marks the checked option of a p-selectbutton with
      // surface.200, barely darker than the unchecked one, so users could not
      // tell which option was selected. The styleguide (buttons, category 07)
      // shows the selected option in primary orange with black text.
      root: {
        checkedBackground: '{primary.color}',
        checkedBorderColor: '{primary.color}',
        checkedColor: '{primary.contrast.color}',
      },
      icon: {
        checkedColor: '{primary.contrast.color}',
      },
      // Material's only keyboard-focus cue is a grey background (its focus
      // ring is off). This css replaces the preset's, so restate that rule;
      // on the checked option grey would hide the selection, so it takes the
      // lighter primary hover orange instead.
      css: ({ dt }) => `
        .p-togglebutton:focus-visible {
          background: ${dt('togglebutton.hover.background')};
        }
        .p-togglebutton.p-togglebutton-checked:focus-visible {
          background: ${dt('primary.hover.color')};
          border-color: ${dt('primary.hover.color')};
        }
      `,
    },
    dialog: {
      css: ({ dt }) => `

        .p-dialog-content:has(.sticky-footer) {
          margin-bottom: 80px;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .p-dialog-content .sticky-footer {
          margin-top: auto;                               /* pushes to bottom when content is short */
          position: fixed;                                /* stays visible when content is long */
          left: 0;
          bottom: 0;                                      /* push the div down to hide the scrolling content */
          flex-shrink: 0;                                 /* prevents footer from shrinking */
          background: ${dt('dialog-background')};         /* covers scrolling content behind it */
          padding: var(--p-overlay-modal-padding);
        }

        .p-dialog-content .has-overflow-shadow {
          box-shadow: 0 -6px 6px -6px rgba(0, 0, 0, 0.3);
        }
      `,
    },
    button: {
      extend: {
        appShell: {
          background: 'var(--app-shell-color)',
          hoverBackground:
            'color-mix(in srgb, var(--app-shell-color), black 15%)',
          activeBackground:
            'color-mix(in srgb, var(--app-shell-color), black 25%)',
          borderColor: 'var(--app-shell-color)',
          hoverBorderColor:
            'color-mix(in srgb, var(--app-shell-color), black 15%)',
          activeBorderColor:
            'color-mix(in srgb, var(--app-shell-color), black 25%)',
          color: 'var(--app-shell-contrast-color)',
          hoverColor: 'var(--app-shell-contrast-color)',
          activeColor: 'var(--app-shell-contrast-color)',
        },
      },
      css: ({ dt }) => `
        .p-dialog-maximize-button {
          background: transparent;
          border-color: transparent;
          color: ${dt('button-text-secondary-color')};
        }

        .p-button-appShell {
          background: ${dt('button.app.shell.background')};
          border-color: ${dt('button.app.shell.border.color')};
          color: ${dt('button.app.shell.color')};
        }
        .p-button-appShell:not(:disabled):hover {
          background: ${dt('button.app.shell.hover.background')};
          border-color: ${dt('button.app.shell.hover.border.color')};
          color: ${dt('button.app.shell.hover.color')};
        }
        .p-button-appShell:not(:disabled):active {
          background: ${dt('button.app.shell.active.background')};
          border-color: ${dt('button.app.shell.active.border.color')};
          color: ${dt('button.app.shell.active.color')};
        }
        .p-button-appShell:focus-visible {
          background: ${dt('button.app.shell.active.background')};
          border-color: ${dt('button.app.shell.active.border.color')};
        }
      `,
    },
    panel: {
      css: () => `
        .p-panel {
          border-color: transparent;
        }

        .p-panel-collapsed .p-panel-content-container {
          height: 0px;
        }
      `,
    },
    toolbar: {
      css: () => `
        .p-toolbar {
          background: var(--p-content-background);
        }

        .p-toolbar h1 {
          font-weight: 600;
          font-size: var(--text-xl); /* 1.25rem (20px) */
          line-height: var(--text-xl--line-height); /* calc(1.75 / 1.25) */
          text-transform: uppercase;
          letter-spacing: 1px;
        }
      `,
    },
    menubar: {
      css: () => `
        .p-menubar {
          border-color: var(--p-content-background);
          border-radius: 0;
        }
      `,
    },
    panelmenu: {
      panel: {
        first: {
          topBorderRadius: '0',
        },
        last: {
          bottomBorderRadius: '0',
        },
      },
      css: () => `
        .p-panelmenu-panel:has(.p-panelmenu-header-active) {
          margin: 0;
        }
      `,
    },
    datatable: {
      header: TABLE_TOOLBAR_HEADER,
      css: () => `
        .p-datatable .p-datatable-tbody > tr {
          transition: background-color 250ms cubic-bezier(0, 0, 0.2, 1);
        }
        .p-datatable .p-datatable-tbody > tr:hover,
        .p-datatable.p-datatable-striped .p-datatable-tbody > tr:hover {
          background: light-dark(var(--p-primary-50), var(--p-highlight-background));
        }
        @media (prefers-reduced-motion: reduce) {
          .p-datatable .p-datatable-tbody > tr { transition: none; }
        }
      `,
    },
    treetable: {
      header: TABLE_TOOLBAR_HEADER,
      css: () => `
        .p-treetable .p-treetable-tbody > tr {
          transition: background-color 250ms cubic-bezier(0, 0, 0.2, 1);
        }
        .p-treetable .p-treetable-tbody > tr:hover {
          background: light-dark(var(--p-primary-50), var(--p-highlight-background));
        }
        @media (prefers-reduced-motion: reduce) {
          .p-treetable .p-treetable-tbody > tr { transition: none; }
        }
      `,
    },
  },
  css: () => `
    .p-overlay:has(.p-select-overlay),
    .p-overlay:has(.p-multiselect-overlay),
    .p-overlay:has(.p-autocomplete-overlay) {
      width: 0;
    }

    /* Keep the option panel no wider than its trigger. In a virtual-scrolled
       list the content box is position:absolute with only min-width:100%, so a
       long option label (e.g. a company name) shrink-to-fits it wider than the
       panel and the scroller shows a horizontal scrollbar. Pin the content to
       the scroller width so labels ellipsize instead of overflowing. */
    .p-select-overlay .p-virtualscroller-content,
    .p-multiselect-overlay .p-virtualscroller-content,
    .p-autocomplete-overlay .p-virtualscroller-content {
      width: 100%;
      max-width: 100%;
    }

    .p-select-overlay .p-virtualscroller,
    .p-multiselect-overlay .p-virtualscroller,
    .p-autocomplete-overlay .p-virtualscroller {
      overflow-x: hidden;
    }

    li.p-select-option,
    li.p-multiselect-option,
    li.p-autocomplete-option {
      min-width: 0;
    }

    /* Flex items default to min-width:auto and refuse to shrink below their
       content, which defeats text-overflow; min-width:0 lets the label clip. */
    li.p-select-option > span,
    li.p-multiselect-option > span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    li.p-multiselect-option > p-checkbox {
      flex-shrink: 0;
    }

    /*
     * The noUiSlider theming that used to live here moved to the only component
     * that renders a slider, next to the vendor stylesheet it overrides:
     * libs/cube-feature/.../color-factory/custom/custom-slider.component.scss
     *
     * It had to move. These rules override nouislider's own with the same
     * specificity (e.g. .noUi-horizontal 2px vs the vendor's 18px), so they only
     * ever won because the vendor sheet was loaded first by every app's global
     * styles.scss. Now that the vendor sheet ships with the component, "first"
     * is no longer guaranteed — and a cascade layer does not save it either,
     * because webpack (Storybook) strips layer() off an @import. Co-locating the
     * overrides after the import makes the order deterministic in every
     * pipeline, and host-prefixing them raises their specificity as well.
     */
    .sketch-picker {
      box-shadow: none !important;
    }
  `,
});

export const PrimeNgMetaConfig: PrimeNGConfigType = {
  theme: {
    preset: PrimeNgMetaTheme,
    options: {
      darkModeSelector: '.not-existing-dark-mode-selector',
    },
  },
  overlayAppendTo: 'body',
  overlayOptions: {
    mode: 'overlay',
    appendTo: 'body',
  },
  inputVariant: 'filled',
  translation: TRANSLATION_DE,
};
